/**
 * SEOHead.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Injects page-level <title>, Open Graph, Twitter Card, and JSON-LD structured
 * data via react-helmet.
 */

import React from 'react';
import { Helmet } from 'react-helmet';
import { MARKET, CURRENCY, SOCIAL_PROFILE_URLS, SUPPORT_TEL } from '../../config/market';

const SITE_ORIGIN =
  (typeof process !== 'undefined' &&
    process.env.REACT_APP_CLIENT_URL &&
    process.env.REACT_APP_CLIENT_URL.replace(/\/$/, '')) ||
  MARKET.siteOrigin;

const API_ORIGIN = (() => {
  const raw =
    (typeof process !== 'undefined' && process.env.REACT_APP_SERVER_URL) || '';
  const trimmed = raw.replace(/\/api\/?$/i, '').replace(/\/$/, '');
  return trimmed || SITE_ORIGIN;
})();

const LOGO = `${SITE_ORIGIN}/logo512.png`;

/** Lusuku's identity in structured data, shared by every page that names it. */
export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
export const ORGANIZATION_LOGO = LOGO;

/**
 * Who Lusuku is, for the homepage: the Organization (name, logo, support line
 * and every public profile, `sameAs`) and the WebSite it publishes. This is
 * what lets Google treat the site, the Business Profile and the Instagram,
 * TikTok, Facebook and LinkedIn accounts as one Lusuku when someone searches
 * the name. Built from the market registry, so lusuku.co.zm describes itself.
 */
export function buildOrganizationJsonLd() {
  const home = `${SITE_ORIGIN}/`;
  const organization = {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Lusuku',
    url: home,
    logo: { '@type': 'ImageObject', url: ORGANIZATION_LOGO, width: 512, height: 512 },
    areaServed: MARKET.name,
  };
  if (SOCIAL_PROFILE_URLS.length) organization.sameAs = [...SOCIAL_PROFILE_URLS];
  const telephone = String(SUPPORT_TEL || '').replace(/^tel:/i, '');
  if (telephone) {
    organization.contactPoint = {
      '@type': 'ContactPoint',
      telephone,
      contactType: 'customer service',
      areaServed: MARKET.country,
    };
  }
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      {
        '@type': 'WebSite',
        '@id': `${home}#website`,
        name: 'Lusuku',
        url: home,
        publisher: { '@id': ORGANIZATION_ID },
      },
    ],
  };
}

/**
 * Strip HTML tags and collapse whitespace (for meta / JSON-LD text).
 */
export function stripHtmlForSeo(text) {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Meta description for brand storefronts (public teaser is indexable; catalogue may be sign-in only).
 */
export function buildBrandSeoDescription(brand) {
  if (!brand || typeof brand !== 'object') {
    return `Discover verified brands on Lusuku — ${MARKET.name}'s online marketplace.`;
  }
  const displayName = brand.tradingName || brand.name || 'this brand';
  const chunks = [];
  if (brand.tagline) {
    const t = stripHtmlForSeo(brand.tagline);
    if (t) chunks.push(t);
  }
  const body = stripHtmlForSeo(brand.description || '');
  if (body) {
    chunks.push(body.length > 120 ? `${body.slice(0, 117).trim()}…` : body);
  }
  let out =
    chunks.length > 0
      ? chunks.join(' · ')
      : `Browse ${displayName} on Lusuku — verified brand storefront, delivery across ${MARKET.name}.`;
  if (brand.catalogueLocked) {
    const hint = ' Sign in on Lusuku to view the product catalogue.';
    const max = 160 - hint.length;
    if (out.length > max) out = `${out.slice(0, Math.max(0, max - 1)).trim()}…`;
    out += hint;
  }
  return out.slice(0, 160);
}

/**
 * Resolve media URLs for og:image / JSON-LD (API-relative paths, protocol-relative, absolute).
 */
export function resolveSeoImageUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('//')) return `https:${u}`;
  const origin = String(API_ORIGIN).replace(/\/$/, '');
  const path = u.startsWith('/') ? u : `/${u.replace(/^\.\//, '')}`;
  return `${origin}${path}`;
}

function buildBrandKeywords(brand) {
  if (!brand || typeof brand !== 'object') return ['Lusuku', MARKET.name];
  const raw = [
    'Lusuku',
    MARKET.name,
    brand.name,
    brand.tradingName,
    brand.legalName,
    brand.brandType,
    brand.tagline && stripHtmlForSeo(brand.tagline),
  ].filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const s of raw) {
    const k = String(s).toLowerCase();
    if (k.length < 2 || seen.has(k)) continue;
    seen.add(k);
    out.push(String(s));
  }
  return out;
}

function buildBrandJsonLd({
  brand,
  canonical,
  fullTitle,
  desc,
  ogImage,
}) {
  const displayName = brand.tradingName || brand.name || 'Brand';
  const legalName = brand.legalName;
  const orgId = `${canonical}#brand`;
  const logoAbs = resolveSeoImageUrl(brand.imageUrl);
  const bannerAbs = resolveSeoImageUrl(brand.bannerUrl);
  const images = [bannerAbs, logoAbs].filter(Boolean);
  const addressStr =
    typeof brand.address === 'string' && brand.address.trim()
      ? brand.address.trim()
      : '';

  const org = {
    '@type': 'Organization',
    '@id': orgId,
    name: displayName,
    url: canonical,
    description: stripHtmlForSeo(
      brand.description || brand.tagline || desc
    ).slice(0, 2000),
  };
  if (legalName && legalName !== displayName) org.legalName = legalName;
  if (logoAbs) {
    org.logo = { '@type': 'ImageObject', url: logoAbs };
  }
  if (images.length) {
    org.image = images.length === 1 ? images[0] : images;
  }
  if (addressStr) {
    org.address = {
      '@type': 'PostalAddress',
      streetAddress: addressStr,
      addressCountry: MARKET.country,
    };
  }

  const page = {
    '@type': 'WebPage',
    '@id': `${canonical}#webpage`,
    url: canonical,
    name: fullTitle,
    description: desc,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Lusuku',
      url: SITE_ORIGIN,
    },
    about: { '@id': orgId },
  };
  if (ogImage) {
    page.primaryImageOfPage = { '@type': 'ImageObject', url: ogImage };
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [org, page],
  };
}

/**
 * index.html (and, for crawlers, the og-inject edge function) ship a
 * description, a canonical and social tags of their own. react-helmet only
 * manages the tags it created, so the static copies stayed beside Helmet's and
 * Google read two descriptions on a page (its snippet for /healthcare was both
 * run together). Once Helmet has written a page's tags, every unmanaged copy of
 * a tag it now manages is removed, so the rendered page states each thing once.
 */
const STATIC_DUPLICATES = [
  'meta[name="description"]',
  'meta[name="keywords"]',
  'meta[name="robots"]',
  'link[rel="canonical"]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
];

export function dropStaticDuplicates() {
  if (typeof document === 'undefined' || !document.head) return;
  STATIC_DUPLICATES.forEach((selector) => {
    if (!document.head.querySelector(`${selector}[data-react-helmet]`)) return;
    document.head
      .querySelectorAll(`${selector}:not([data-react-helmet])`)
      .forEach((el) => el.remove());
  });
}

/**
 * Structured data the edge function already wrote into the crawler's HTML,
 * server-built from the catalogue: "product" (GTIN, stock, sale price) or
 * "storefront" (Restaurant, Store or Pharmacy with its menu or catalogue).
 * When it is there, this component does not add a second, thinner copy: two
 * descriptions of one thing on a page read to Google as two things.
 */
export const hasServerData = (kind) =>
  typeof document !== 'undefined' &&
  Boolean(document.querySelector(`script[type="application/ld+json"][data-lusuku-seo="${kind}"]`));

/**
 * @param {object} props
 * @param {string}  [props.title]
 * @param {string}  [props.searchTitle] - The <title> for search results when it
 *   differs from the name (storefronts: "<name>: menu and delivery"). Social
 *   cards keep `title`.
 * @param {string}  [props.description]
 * @param {string}  [props.imageUrl]
 * @param {string}  [props.canonicalPath]
 * @param {string}  [props.type]
 * @param {string[]} [props.keywords]
 * @param {object}  [props.product]
 * @param {object}  [props.brand] - Brand storefront; adds Organization + WebPage JSON-LD
 * @param {boolean} [props.organization] - The homepage: adds Lusuku's own
 *   Organization + WebSite JSON-LD (buildOrganizationJsonLd)
 * @param {boolean} [props.noindex]
 */
export const SEOHead = ({
  title,
  searchTitle = '',
  description,
  imageUrl = LOGO,
  canonicalPath = '',
  type = 'website',
  keywords = [],
  product = null,
  brand = null,
  organization = false,
  noindex = false,
}) => {
  const fullTitle = title
    ? `${title} | Lusuku`
    : `Lusuku — ${MARKET.name}'s Online Marketplace`;
  const documentTitle = searchTitle ? `${searchTitle} | Lusuku` : fullTitle;
  const desc =
    description ||
    `Order food, groceries, medicine & more. Fast delivery across ${MARKET.name}.`;
  const canonical = `${SITE_ORIGIN}${canonicalPath}`;

  const resolvedOgImage = resolveSeoImageUrl(imageUrl) || LOGO;

  const effectiveKeywords =
    keywords.length > 0
      ? keywords
      : product?.name
        ? [product.name]
        : brand
          ? buildBrandKeywords(brand)
          : [];
  const keywordsString = effectiveKeywords.join(', ');

  /* Never a price of 0: a product with no public price (price on request,
     not yet priced) is described without an offer rather than offered free. */
  const productPrice = Number(product?.price);
  const hasPublicPrice = Number.isFinite(productPrice) && productPrice > 0;

  const productSchema =
    product && !hasServerData('product')
      ? {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description: product.description || desc,
          image: resolveSeoImageUrl(product.imageUrl) || resolvedOgImage,
          sku: String(product._id),
          keywords: effectiveKeywords.join(', '),
          brand: { '@type': 'Brand', name: product.vendorName || 'Lusuku' },
          ...(hasPublicPrice
            ? {
                offers: {
                  '@type': 'Offer',
                  priceCurrency: CURRENCY,
                  price: String(productPrice),
                  availability:
                    product.available !== false
                      ? 'https://schema.org/InStock'
                      : 'https://schema.org/OutOfStock',
                  itemCondition: 'https://schema.org/NewCondition',
                  seller: { '@type': 'Organization', name: product.vendorName || 'Lusuku' },
                  url: canonical,
                },
              }
            : {}),
        }
      : null;

  const brandSchema =
    brand && !product && !hasServerData('storefront')
      ? buildBrandJsonLd({
          brand,
          canonical,
          fullTitle,
          desc,
          ogImage: resolvedOgImage,
        })
      : null;

  const structuredScripts = [];
  if (productSchema) {
    structuredScripts.push(
      <script key="product-ld" type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>
    );
  }
  if (brandSchema) {
    structuredScripts.push(
      <script key="brand-ld" type="application/ld+json">
        {JSON.stringify(brandSchema)}
      </script>
    );
  }
  if (organization) {
    structuredScripts.push(
      <script key="organization-ld" type="application/ld+json">
        {JSON.stringify(buildOrganizationJsonLd())}
      </script>
    );
  }

  return (
    <Helmet onChangeClientState={dropStaticDuplicates}>
      <title>{documentTitle}</title>
      <meta name="description" content={desc} />
      {keywordsString ? <meta name="keywords" content={keywordsString} /> : null}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : null}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Lusuku" />
      <meta property="og:locale" content={MARKET.currencyLocale.replace('-', '_')} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={resolvedOgImage} />

      {/* Facebook Product tags (for catalog ads) */}
      {product && hasPublicPrice ? (
        <meta
          property="product:price:amount"
          content={String(productPrice)}
        />
      ) : null}
      {product && hasPublicPrice ? (
        <meta property="product:price:currency" content={CURRENCY} />
      ) : null}

      {structuredScripts}
    </Helmet>
  );
};

export default SEOHead;
